import { motion } from "framer-motion";
import { forwardRef, useContext, useEffect, useRef, useState } from "react";
import LazyLoad from "react-lazy-load";
import formatCurrency from "../../func/currency_formatter";
import { TemplateContext } from "../../layout";
import ProductSelectorButton from "../ProductSelectorButton.jsx";
import { VariantPicker } from "./VariantPicker";
import DoubleTagIcon from "/fallbacks/DoubleTagIcon.svg";

const ProductPackageItem = forwardRef((props, ref) => {
  const { product, handleChange, active, index } = props;
  const [templateContext, setTemplateContext, variantsContext, setVariantsContext] = useContext(TemplateContext);
  const [hasCents, setHasCents] = useState(true);
  const [render, setRender] = useState();
  const [fadeIn, setFadeIn] = useState();
  const [variantImage, setVariantImage] = useState();
  const variantsRef = useRef();
  const withLabel = product.best_seller;
  const [priceObject, setPriceObject] = useState({
    number: 0,
    cents: 0,
    symbol: 0,
    symbolLeft: true,
  });

  useEffect(() => {
    if (active) {
      setRender(true);
      setTimeout(() => {
        setFadeIn(active);
      }, 300);
    } else {
      setFadeIn(false);
      setTimeout(() => {
        setRender(false);
      }, 150);
    }
  }, [active]);

  const productsContent = window?.post_template.checkout_v4_products;
  const cardBackground = productsContent.checkout_v4_products_CardBackground;
  const cardBackgroundSelected = productsContent.checkout_v4_products_selectedCardBackground;
  const cardBorder = productsContent.checkout_v4_products_borderColor;
  const cardBorderSelected = productsContent.checkout_v4_products_borderColorSelected;
  const textPrimary = productsContent.checkout_v4_products_textPrimary;
  const textSecondary = productsContent.checkout_v4_products_textSecondary;
  const outlineBorder = productsContent.checkout_v4_products_imageWrap;
  const showBadges = productsContent.checkout_v4_products_badgeShow;
  const colorVariantHeading = productsContent?.tr_checkout_v4_colorVariantHeading;
  const sizeVariantHeading = productsContent?.tr_checkout_v4_sizeVariantHeading;
  document.documentElement.style.setProperty("--card-background-active", cardBackgroundSelected);
  document.documentElement.style.setProperty("--card-border-active", cardBorderSelected);
  document.documentElement.style.setProperty("--card-border", cardBorder);
  document.documentElement.style.setProperty("--card-background", cardBackground);
  document.documentElement.style.setProperty("--card-text-primary", textPrimary);
  document.documentElement.style.setProperty("--card-text-secondary", textSecondary);

  function removeCurrencySymbol(currencyString) {
    const currencyRegex = /(?:^\D+)|(?:\D+$)/;
    const currencySymbol = currencyString.match(currencyRegex)[0];
    const value = currencyString.replace(currencyRegex, "");
    return { currencySymbol, value };
  }

  function identifyCurrencySymbol(array) {
    const currencyRegex = /(?:^\D+)|(?:\D+$)/;
    const firstItemHasSymbol = currencyRegex.test(array[0].trim());

    if (firstItemHasSymbol) {
      const splitedFirstNumber = removeCurrencySymbol(array[0].trim());

      setPriceObject({
        ...priceObject,
        symbolLeft: true,
        number:
          window.productData.currency_code === "ZAR" ? splitedFirstNumber.value : Number(splitedFirstNumber.value),
        cents: Number(array[1]) !== 0 ? Number(array[1]) : "00",
        symbol: splitedFirstNumber.currencySymbol,
      });
      return;
    } else {
      const splitedSecondNumber = removeCurrencySymbol(array[1]);
      setPriceObject({
        ...priceObject,
        symbolLeft: false,
        number: array[0],
        cents: splitedSecondNumber.value ?? null,
        symbol: splitedSecondNumber.currencySymbol.trim(),
      });
      return;
    }
  }

  const isDiscountApplied = templateContext.discountCode === window.productData?.discount_codes?.discount_code;

  const getFormattedPrice = (product, isDiscountApplied, discountsEnabled) => {
    if (discountsEnabled) {
      if (isDiscountApplied && product?.promo_discounted_price) {
        return product?.formatted_promo_price || formatCurrency(Number(product?.promo_discounted_price));
      }
    }
    return product?.formatted_price || formatCurrency(Number(product?.price));
  };

  const getDiscountPercentage = (product, isDiscountApplied) => {
    if (isDiscountApplied && product.promo_discount_amount_percent) {
      return product.promo_discount_amount_percent;
    }
    return product.you_save_percentage;
  };

  useEffect(() => {
    const formattedCurrency = getFormattedPrice(product, isDiscountApplied, templateContext.discountsEnabled);

    let splitedValue;

    if (formattedCurrency.includes("R ")) {
      const parts = product.price.split(".");
      if (parts[0].length >= 3) {
        parts[0] = Number(parts[0]).toLocaleString();
      }
      splitedValue = ["R" + parts[0], parts[1]];
    } else {
      if (formattedCurrency.includes(",")) {
        splitedValue = formattedCurrency.split(",");
      } else {
        splitedValue = formattedCurrency.split(".");
      }
    }

    if (window.productData?.currency_code === "JPY") {
      setHasCents(false);
    }
    identifyCurrencySymbol(splitedValue);
  }, [templateContext.discountCode, templateContext.discountsEnabled]);

  const fallbackImages = window.post_template.checkout_v4_products.checkout_v4_product_images;
  const matchingImage =
    fallbackImages && fallbackImages.find((obj) => obj.product_amount === product.amount)?.product_image;

  useEffect(() => {
    if (window?.productData?.variants_picker_type == "separate" && variantsContext?.variants) {
      setVariantImage(
        window?.color_variants?.find((item) => {
          return item.value == variantsContext?.variants[0]?.color;
        })?.variant_image
      );
    } else {
      return;
    }
  }, [variantsContext?.variants]);

  const defaultImage = matchingImage ? matchingImage : product.image.url;
  const image = variantImage ? variantImage : defaultImage;
  let discountedPrice = product.formatted_old_price ? product.formatted_old_price : formatCurrency(product.old_price);

  return (
    <div
      data-cy="product-container"
      onClick={() => {
        handleChange(index);
      }}
      id={`productCard-${product.amount}`}
      className={
        active
          ? `productPackages__container productPackage__container--visible`
          : `productPackages__container productPackages__container--unchecked`
      }
    >
      <div className="productPacItem__firstBlock">
        {templateContext.showBulletPoints !== undefined &&
          templateContext.showBulletPoints !== null &&
          !templateContext.showBulletPoints && <ProductSelectorButton />}
        <motion.div
          key={image}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: 1,
            default: { duration: 0.5 },
          }}
        >
          <LazyLoad>
            <img
              className={`productPacItem__image mon-img-85-w mon-img-85-h ${outlineBorder ? "productPacItem__image--border" : ""}`}
              src={image}
              alt={product.image.alt}
            />
          </LazyLoad>
        </motion.div>
        <div className="productPacItem__label__container">
          <span data-cy="product-amount" className="productPacItem__label__amount">
            {product.amount}x
          </span>
          <span className="productPacItem__label">{product.display_name}</span>
          <div className="productPacItem__discount__box desktopHide">
            <img src={DoubleTagIcon} /> {getDiscountPercentage(product, isDiscountApplied)}%
          </div>
        </div>
      </div>
      <div className="productPacItem__divider mobileHide"></div>
      <div className="productPacItem__discount__container mobileHide">
        <div className="productPacItem__discount__box" style={!showBadges ? { opacity: "0" } : {}}>
          <img src={DoubleTagIcon} /> {getDiscountPercentage(product, isDiscountApplied)}%
        </div>
      </div>

      <div className="productPacItem__divider"></div>
      <div className="productPacItem__priceBlock__container">
        <div data-cy="product-price-old" className="productPacItem__priceBlock__oldPrice">
          {discountedPrice}
        </div>
        <div className="productPacItem__priceBlock__mainPriceBlock">
          {priceObject.symbolLeft && <sup>{priceObject.symbol}</sup>}
          <div data-cy="product-price" className="productPacItem__priceBlock__price">
            {priceObject.number}
          </div>

          <div className="productPacItem__priceBlock__centsBlock">
            <div data-cy="product-price-cents" className="productPacItem__priceBlock__cents">
              {hasCents && `. ${priceObject.cents}`}
            </div>

            <div
              className={`productPacItem__priceBlock__each
                ${
                  hasCents
                    ? `productPacItem__priceBlock__each--with-cents`
                    : `productPacItem__priceBlock__each--without-cents`
                } `}
            >
              {window.post_template.checkout_v4_products.tr_checkout_v4_products_eachLabel}
            </div>
          </div>
          {!priceObject.symbolLeft && (
            <div className="productPacItem__priceBlock" style={{ marginLeft: "2px" }}>
              <sup>{priceObject.symbol}</sup>
            </div>
          )}
        </div>
        {withLabel && <div className="productPacItem__priceBlock__labelTag">{product.status.toUpperCase()}</div>}
      </div>
      {render && (
        <div
          className={`variants-container ${fadeIn ? "variants-container--show" : "variants-container--hide"}`}
          style={fadeIn ? { maxHeight: `${variantsRef?.current?.clientHeight}px` } : { maxHeight: "0px" }}
        >
          <div ref={variantsRef}>
            {window?.productData?.enable_color_variants && window?.productData?.variants_picker_type == "each" && (
              <VariantPicker
                defaultValue={variantsContext.defaultColorVariant}
                type="color"
                heading={colorVariantHeading}
                id={product?.amount}
                data={window?.color_variants}
                numberOfItems={product?.amount}
              />
            )}
            {window?.productData?.enable_size_variants && window?.productData?.variants_picker_type == "each" && (
              <VariantPicker
                defaultValue={variantsContext.defaultSizeVariant}
                type="size"
                heading={sizeVariantHeading}
                id={product?.amount}
                data={window?.size_variants}
                numberOfItems={product?.amount}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export { ProductPackageItem };
